export type GooglePlaceOption = {
  description: string;
  placePrediction: google.maps.places.PlacePrediction;
};

const PLACE_AUTOCOMPLETE_REGION_CODES = ["za"];
const PLACE_FETCH_FIELDS = [
  "displayName",
  "addressComponents",
  "formattedAddress",
  "location",
] as const;

export const createAutocompleteSessionToken = () =>
  new google.maps.places.AutocompleteSessionToken();

export async function fetchPlaceAutocompleteOptions(
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
) {
  const { suggestions } =
    await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      {
        input,
        includedRegionCodes: PLACE_AUTOCOMPLETE_REGION_CODES,
        sessionToken,
      },
    );

  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter(
      (
        prediction,
      ): prediction is google.maps.places.PlacePrediction =>
        prediction != null,
    )
    .map((prediction) => ({
      description: prediction.text.text,
      placePrediction: prediction,
    }))
    .filter((option) => option.description.length > 0);
}

function buildResolvedPlaceDescription(
  place: google.maps.places.Place,
  fallbackDescription: string,
) {
  const displayName = place.displayName?.trim();
  const formattedAddress = place.formattedAddress?.trim();

  if (displayName && formattedAddress) {
    return formattedAddress.startsWith(displayName)
      ? formattedAddress
      : `${displayName}, ${formattedAddress}`;
  }

  return formattedAddress || displayName || fallbackDescription;
}

export async function resolvePlaceSelection(option: GooglePlaceOption) {
  const place = option.placePrediction.toPlace();

  await place.fetchFields({
    fields: [...PLACE_FETCH_FIELDS],
  });

  const location = place.location;
  if (!location) {
    return null;
  }

  return {
    description: buildResolvedPlaceDescription(place, option.description),
    latitude: location.lat(),
    longitude: location.lng(),
  };
}
